import {useCallback} from 'react'
import {GearIcon} from '@primer/octicons-react'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {LabelPicker as ItemPickerLabel, type LabelPickerLabel} from '@github-ui/item-picker-label-tsq'
import {useModifyLabels} from '../../mutations/use-modify-labels'
import {useLabels} from './data/use-labels'
import {useOverviewRoute} from './data/use-overview-route'
import {SidebarDisclosureHeader} from './SidebarDisclosureHeader'

export function LabelsPicker() {
  const {
    data: {labels},
  } = useLabels()
  const {owner, repo} = useRouteParams(useOverviewRoute())
  const {mutate, isPending} = useModifyLabels()

  const pickerLabels: LabelPickerLabel[] = labels.map(label => ({
    id: label.nodeId ?? '',
    name: label.name,
    nameHTML: label.name,
    color: label.color,
    description: label.description || null,
    url: `/${owner}/${repo}/labels/${encodeURIComponent(label.name)}`,
  }))

  const handleSelectionChange = useCallback(
    (nextLabels: LabelPickerLabel[]) => {
      const previousIds = new Set(labels.map(label => label.nodeId ?? ''))
      const nextIds = new Set(nextLabels.map(label => label.id))
      const addLabels = nextLabels.filter(label => !previousIds.has(label.id)).map(label => label.id)
      const removeLabels = labels.map(label => label.nodeId ?? '').filter(id => id && !nextIds.has(id))

      if (addLabels.length > 0 || removeLabels.length > 0) {
        mutate({addLabels, removeLabels, nextLabels})
      }
    },
    [labels, mutate],
  )

  return (
    <ItemPickerLabel
      repo={repo}
      owner={owner}
      labels={pickerLabels}
      canCreateLabel={false}
      isLoading={isPending}
      onSelectionChange={handleSelectionChange}
      anchorElement={(anchorProps, ref) => (
        <SidebarDisclosureHeader
          title="Labels"
          trailingIcon={GearIcon}
          ref={ref}
          aria-label="Manage labels"
          {...anchorProps}
        />
      )}
    />
  )
}
